import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';

const PasswordPromptModal = ({
  isOpen,
  onClose,
  fileName,
  onSubmit
}) => {
  const [password, setPassword] = useState('');
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [error, setError] = useState('');

  // Handle modal visibility with animation
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setPassword(''); // Reset password when modal opens
      setError(''); // Clear any previous errors
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      const timer = setTimeout(() => setIsVisible(false), 150);
      return () => clearTimeout(timer);
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Handle password submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!password.trim()) {
      setError('Please enter a password');
      return;
    }

    setIsUnlocking(true);
    setError('');

    try {
      await onSubmit(password);
      // If successful, modal will be closed by parent component
    } catch (err) {
      setError(err.message || 'Incorrect password. Please try again.');
      setIsUnlocking(false);
    }
  };

  // Handle backdrop click
  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget && !isUnlocking) {
      onClose();
    }
  };

  if (!isVisible) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-300 ${
        isOpen
          ? 'opacity-100 backdrop-blur-sm bg-black/50'
          : 'opacity-0 pointer-events-none'
      }`}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="password-modal-title"
    >
      <div
        className={`relative w-full max-w-md bg-white rounded-xl shadow-2xl transform transition-all duration-300 ${
          isOpen ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-4 border-b border-gray-100">
          <div className="flex items-center space-x-3">
            <LockClosedIcon className="w-6 h-6 text-amber-600" />
            <h2
              id="password-modal-title"
              className="text-xl font-semibold text-gray-900"
            >
              Password Protected PDF
            </h2>
          </div>
          <button
            onClick={onClose}
            disabled={isUnlocking}
            className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
            aria-label="Close modal"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-4">
            {/* File name */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-900">
                <span className="font-medium">📄 {fileName}</span>
              </p>
              <p className="text-xs text-amber-700 mt-1">
                This PDF requires a password to access its contents.
              </p>
            </div>

            {/* Password input */}
            <div className="space-y-2">
              <label htmlFor="pdf-password" className="block text-sm font-medium text-gray-900">
                Enter Password
              </label>
              <input
                id="pdf-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isUnlocking}
                className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                  error ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter PDF password"
                autoComplete="off"
                autoFocus
              />
              {error && (
                <p className="text-xs text-red-600 flex items-center space-x-1">
                  <span>❌</span>
                  <span>{error}</span>
                </p>
              )}
              <p className="text-xs text-gray-500">
                💡 We'll use this password to verify the page count for accurate credit calculation.
              </p>
            </div>

            {/* Info box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-900">
                <strong>Why we need this:</strong> We need to count the number of pages to calculate the exact credit cost (1 credit per page) before processing.
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 p-6 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              disabled={isUnlocking}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!password.trim() || isUnlocking}
              className={`flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-lg transition-all ${
                password.trim() && !isUnlocking
                  ? 'bg-emerald-600 hover:bg-emerald-700 focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 shadow-sm'
                  : 'bg-gray-300 cursor-not-allowed'
              }`}
            >
              {isUnlocking ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Unlocking...</span>
                </div>
              ) : (
                'Unlock PDF'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PasswordPromptModal;
